import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { P, deriveInsights, generateInterpretation } from "../components/report/report-utils";
import { ReportHeader } from "../components/report/ReportHeader";
import { PageNav } from "../components/report/PageNav";
import { SnapshotTab } from "../components/report/SnapshotTab";
import { DimensionsTab } from "../components/report/DimensionsTab";
import { ArchetypeTab } from "../components/report/ArchetypeTab";
import { CoachingTab } from "../components/report/CoachingTab";
import { NotesTab } from "../components/report/NotesTab";

export default function ReportPage() {
    const { userId } = useParams();
    const [report, setReport] = useState(null);
    const [notes, setNotes] = useState({ notes: "", next_session_plan: "" });
    const [loading, setLoading] = useState(true);
    const [savingNotes, setSavingNotes] = useState(false);
    const [page, setPage] = useState("snapshot");

    useEffect(() => {
        Promise.all([
            api.get(`/reports/${userId}`),
            api.get(`/coach/notes/${userId}`).catch(() => ({ data: { notes: "", next_session_plan: "" } }))
        ]).then(([r, n]) => { setReport(r.data); setNotes(n.data); })
            .catch(console.error).finally(() => setLoading(false));
    }, [userId]);

    const handleExportPDF = async () => {
        try {
            const res = await api.get(`/reports/${userId}/pdf`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.setAttribute("download", `Coachability_Report_${report?.user?.name?.replace(/\s/g, "_") || "User"}.pdf`);
            document.body.appendChild(a); a.click(); a.remove();
        } catch { toast.error("PDF download failed"); }
    };

    const saveCoachNotes = async () => {
        setSavingNotes(true);
        try { await api.post(`/coach/notes/${userId}`, notes); toast.success("Notes saved"); }
        catch { toast.error("Failed to save notes"); }
        finally { setSavingNotes(false); }
    };

    if (loading) return (
        <div className="flex items-center justify-center h-full py-20">
            <Loader2 className="h-7 w-7 animate-spin text-slate-400" />
        </div>
    );
    if (!report) return <div className="p-8 text-slate-400">Report not available</div>;

    const { user: rUser, coach, scores } = report;
    const total = scores?.total_weighted_score || 0;
    const dimScores = scores?.dimension_scores || {};
    const archetype = scores?.archetype || {};
    const defense = scores?.defense_index || {};
    const consistency = scores?.consistency_index || {};
    const aps = scores?.aps || {};
    const risks = scores?.hidden_risks || [];
    const { strengths, devAreas } = deriveInsights(dimScores);
    const interpretation = generateInterpretation(total, archetype, aps, defense);

    return (
        <div data-testid="report-page" className="pb-6" style={{ background: P.light, minHeight: "100vh" }}>
            <ReportHeader rUser={rUser} coach={coach} onExportPDF={handleExportPDF} />

            <div className="px-3 sm:px-5 lg:px-7 max-w-[1100px]">
                <div className="mt-6 mb-2">
                    <PageNav active={page} onChange={setPage} />
                </div>

                {page === "snapshot" && (
                    <SnapshotTab total={total} archetype={archetype} aps={aps} defense={defense} consistency={consistency} interpretation={interpretation} />
                )}
                {page === "dimensions" && (
                    <DimensionsTab dimScores={dimScores} total={total} strengths={strengths} devAreas={devAreas} />
                )}
                {page === "archetype" && <ArchetypeTab archetype={archetype} />}
                {page === "coaching" && <CoachingTab archetype={archetype} risks={risks} consistency={consistency} aps={aps} />}
                {page === "notes" && <NotesTab notes={notes} setNotes={setNotes} onSave={saveCoachNotes} saving={savingNotes} />}

                <div className="pt-8 pb-3 text-center" data-testid="report-k2b-footer">
                    <span className="text-[0.6rem] text-slate-300 tracking-wider" style={{ fontFamily: "Manrope" }}>
                        Powered by K2B Learning Pvt. Ltd.
                    </span>
                </div>
            </div>
        </div>
    );
}
